import { deriveKey } from './scrypt'
import { CONFIG } from '../config'

// Generate random bytes
export function randomBytes(length: number): Uint8Array {
  return crypto.getRandomValues(new Uint8Array(length))
}

// Generate vault ID
export function generateVaultId(): string {
  const bytes = randomBytes(CONFIG.VAULT_ID_LENGTH)
  let id = ''
  for (let i = 0; i < CONFIG.VAULT_ID_LENGTH; i++) {
    id += CONFIG.VAULT_ID_ALPHABET[bytes[i] % CONFIG.VAULT_ID_ALPHABET.length]
  }
  return id
}

// Validate vault ID format
export function isValidVaultId(id: string): boolean {
  if (id.length !== CONFIG.VAULT_ID_LENGTH) return false
  for (const char of id) {
    if (!CONFIG.VAULT_ID_ALPHABET.includes(char.toUpperCase())) return false
  }
  return true
}

// Base64 encoding/decoding
export function toBase64(data: Uint8Array): string {
  return btoa(String.fromCharCode(...data))
}

export function fromBase64(str: string): Uint8Array {
  const binary = atob(str)
  const bytes = new Uint8Array(binary.length)
  for (let i = 0; i < binary.length; i++) {
    bytes[i] = binary.charCodeAt(i)
  }
  return bytes
}

// Derive key from passphrase using scrypt
export async function deriveKeyFromPassphrase(
  passphrase: string,
  salt: Uint8Array
): Promise<Uint8Array> {
  return deriveKey(passphrase, salt, {
    N: CONFIG.SCRYPT_N,
    r: CONFIG.SCRYPT_R,
    p: CONFIG.SCRYPT_P,
    dkLen: CONFIG.SCRYPT_DKLEN,
  })
}

// HKDF-SHA256 for key derivation
export async function hkdf(
  key: Uint8Array,
  salt: Uint8Array,
  info: string,
  length: number
): Promise<Uint8Array> {
  const cryptoKey = await crypto.subtle.importKey(
    'raw',
    key,
    { name: 'HKDF' },
    false,
    ['deriveBits']
  )

  const derived = await crypto.subtle.deriveBits(
    {
      name: 'HKDF',
      hash: 'SHA-256',
      salt: salt,
      info: new TextEncoder().encode(info),
    },
    cryptoKey,
    length * 8
  )

  return new Uint8Array(derived)
}

// Derive verification tag from key
export async function deriveVerificationTag(
  key: Uint8Array,
  salt: Uint8Array
): Promise<Uint8Array> {
  return hkdf(key, salt, CONFIG.HKDF_VERIFY_CONTEXT, 32)
}

// Derive cache encryption key
export async function deriveCacheKey(
  key: Uint8Array,
  salt: Uint8Array
): Promise<Uint8Array> {
  return hkdf(key, salt, CONFIG.HKDF_CACHE_CONTEXT, 32)
}

// Import raw key for AES-GCM
async function importAESKey(key: Uint8Array): Promise<CryptoKey> {
  return crypto.subtle.importKey('raw', key, { name: 'AES-GCM' }, false, [
    'encrypt',
    'decrypt',
  ])
}

// Encrypt data with AES-256-GCM
export async function encrypt(
  data: Uint8Array,
  key: Uint8Array
): Promise<Uint8Array> {
  const iv = randomBytes(CONFIG.AES_IV_LENGTH)
  const cryptoKey = await importAESKey(key)

  const ciphertext = await crypto.subtle.encrypt(
    { name: 'AES-GCM', iv },
    cryptoKey,
    data
  )

  // Format: [IV (12 bytes)][Ciphertext + Auth Tag]
  const result = new Uint8Array(iv.length + ciphertext.byteLength)
  result.set(iv, 0)
  result.set(new Uint8Array(ciphertext), iv.length)
  return result
}

// Decrypt data with AES-256-GCM
export async function decrypt(
  encryptedData: Uint8Array,
  key: Uint8Array
): Promise<Uint8Array> {
  const iv = encryptedData.slice(0, CONFIG.AES_IV_LENGTH)
  const ciphertext = encryptedData.slice(CONFIG.AES_IV_LENGTH)

  const cryptoKey = await importAESKey(key)

  const plaintext = await crypto.subtle.decrypt(
    { name: 'AES-GCM', iv },
    cryptoKey,
    ciphertext
  )

  return new Uint8Array(plaintext)
}

// Constant-time comparison (timing-safe)
export function constantTimeCompare(a: Uint8Array, b: Uint8Array): boolean {
  if (a.length !== b.length) return false
  let result = 0
  for (let i = 0; i < a.length; i++) {
    result |= a[i] ^ b[i]
  }
  return result === 0
}

// Compress data using gzip (via fflate)
export async function compress(data: Uint8Array): Promise<Uint8Array> {
  const { gzipSync } = await import('fflate')
  return gzipSync(data, { level: 6 })
}

// Decompress gzipped data
export async function decompress(data: Uint8Array): Promise<Uint8Array> {
  const { gunzipSync } = await import('fflate')
  return gunzipSync(data)
}

// Encrypt vault data (compress -> encrypt)
export async function encryptVaultData(
  data: string,
  key: Uint8Array
): Promise<Uint8Array> {
  const encoded = new TextEncoder().encode(data)
  const compressed = await compress(encoded)
  return encrypt(compressed, key)
}

// Decrypt vault data (decrypt -> decompress)
export async function decryptVaultData(
  encryptedData: Uint8Array,
  key: Uint8Array
): Promise<string> {
  const decrypted = await decrypt(encryptedData, key)
  const decompressed = await decompress(decrypted)
  return new TextDecoder().decode(decompressed)
}
